// ============================================================
// ELC Shift — Uploads / Budget (annual targets) module
//
// Reads an annual budget xlsx (one sheet, dates down rows, stores
// across columns), parses values into {store: {date: value}} shape,
// runs a sanity-check vs existing DB targets (flagging any store
// whose total changes by ≥25%), then upserts to `targets` table
// + writes a localStorage cache for the rota's offline fallback.
//
// Public surface (callable from admin.html inline handlers):
//   Uploads.budget.handleDrop(event)        — file dropzone drop
//   Uploads.budget.handleFile(file)         — programmatic file input
//   Uploads.budget.acceptSuggestion(...)    — "Did you mean...?" accept
//   Uploads.budget.dismissSuggestion(store) — "Did you mean...?" skip
//   Uploads.budget.saveTargets()            — sanity-check + save to DB
//   Uploads.budget.clearTargets()           — wipe local cache
//
// Free globals (referenced as bare identifiers — they live as
//   lexical globals in admin.html, NOT as window properties.
//   Top-level const/let don't attach to window; bare references
//   resolve via the global lexical scope at runtime. Matches the
//   src/top50/ convention):
//   - XLSX                 (SheetJS)
//   - buildUploadNameMap   (admin.html helper)
//   - escapeHtml           (admin.html escape utility)
//   - ALL_STORES           (admin.html canonical store list)
//   - DB                   (db.js DB facade)
//   - showToast            (admin.html toast helper)
//   - renderTargetsStatus  (admin.html re-render hook)
// ============================================================

import { ensureXlsx } from '../shared/ensure-xlsx.js';   // v136.8: the spreadsheet library loads only when it is used
import {
  findStoreColumnSuggestions,
  renderStoreSuggestionCards,
} from './shared.js';

// Module state — fresh on every parsed file.
let budgetParseState = null;
let parsedTargets    = null;

export function handleDrop(e) {
  e.preventDefault();
  const drop = document.getElementById('uploadDropZone');
  if (drop) {
    drop.style.borderColor = 'var(--border)';
    drop.style.background = 'var(--surface-2)';
  }
  const file = e.dataTransfer.files[0];
  if (file) handleFile(file);
}

export function handleFile(file) {
  if (!file || !file.name.endsWith('.xlsx')) {
    showUploadStatus('Please upload an .xlsx file', 'error'); return;
  }
  // New file → fresh state.
  budgetParseState = null;
  showUploadStatus('Reading ' + file.name + '...', 'info');
  // v136.8: the spreadsheet library loads on first use; this runs again once it has (or says why it could not).
  if (typeof XLSX === 'undefined') { ensureXlsx().then(function () { handleFile(file); }, function (e) { showUploadStatus(e.message, 'error'); }); return; }
  const reader = new FileReader();
  reader.onload = function(e) {
    try {
      const wb = XLSX.read(e.target.result, {type:'array', cellDates:true});
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws, {header:1, raw:false, dateNF:'yyyy-mm-dd'});
      parseRows(rows, file.name);
    } catch(err) {
      showUploadStatus('Error reading file: ' + err.message, 'error');
    }
  };
  reader.readAsArrayBuffer(file);
}

function parseRows(rows, filename) {
  // Build state and re-render. Suggestion accept/dismiss replays this path.
  const dynamicMap = buildUploadNameMap();
  const storeMapUpper = {};
  Object.entries(dynamicMap).forEach(([k,v]) => { storeMapUpper[k.trim().toUpperCase()] = v; });

  budgetParseState = {
    rows,
    storeMapUpper,
    accepted: [],
    dismissed: new Set(),
    filename,
  };
  rebuildFromState();
}

function rebuildFromState() {
  if (!budgetParseState) return;
  const { rows, storeMapUpper, accepted, dismissed } = budgetParseState;

  // Find header row — contains 'Date'
  let headerRow = -1;
  for (let i = 0; i < Math.min(5, rows.length); i++) {
    const r = rows[i];
    if (r && r.some(c => String(c||'').toUpperCase().includes('DATE'))) {
      headerRow = i; break;
    }
  }
  if (headerRow === -1) { showUploadStatus('Could not find header row with Date column', 'error'); return; }

  const headers = rows[headerRow];

  let dateColIdx = -1;
  headers.forEach((h, i) => { if (String(h||'').trim().toUpperCase() === 'DATE') dateColIdx = i; });
  if (dateColIdx < 0) dateColIdx = 1; // fallback to col B

  // Match store columns from upload-name map; track unmatched for suggestions.
  const storeCols = [];
  const matchedIdx = new Set();
  headers.forEach((h, i) => {
    const key = String(h||'').trim().toUpperCase();
    const appName = storeMapUpper[key];
    if (appName) {
      storeCols.push({idx:i, xlsxName:String(h).trim(), appName});
      matchedIdx.add(i);
    }
  });

  // Apply accepted (user-picked "Did you mean?") extras.
  accepted.forEach(e => {
    if (!matchedIdx.has(e.idx)) {
      storeCols.push({idx:e.idx, xlsxName:String(headers[e.idx] || '').trim(), appName:e.appName});
      matchedIdx.add(e.idx);
    }
  });

  // Track unmatched headers for suggestions.
  const unmatchedHeaders = [];
  headers.forEach((h, i) => {
    if (i === dateColIdx || matchedIdx.has(i)) return;
    const label = String(h || '').trim();
    if (!label) return;
    const upper = label.toUpperCase();
    if (['DAY','MONTH','WEEK','UK MONTH','UK WEEK','DATE LY','DATE TY'].includes(upper)) return;
    unmatchedHeaders.push({ idx: i, header: label });
  });

  if (!storeCols.length) {
    showUploadStatus(`No recognised store columns found. Header row ${headerRow+1} has: ${headers.filter(Boolean).slice(0,5).join(', ')}... — check that store Upload Names in the Stores tab match the spreadsheet headers.`, 'error');
    return;
  }

  const targets = {};
  let rowCount = 0;
  for (let r = headerRow+1; r < rows.length; r++) {
    const row = rows[r];
    if (!row || !row[dateColIdx]) continue;
    let dateStr = String(row[dateColIdx]).trim();
    // Normalise date
    if (dateStr.includes('/')) {
      const parts = dateStr.split('/');
      if (parts.length === 3) {
        dateStr = parts[2]+'-'+parts[1].padStart(2,'0')+'-'+parts[0].padStart(2,'0');
      }
    } else if (dateStr.length > 10) {
      dateStr = dateStr.slice(0,10);
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) continue;

    storeCols.forEach(sc => {
      const val = parseFloat(row[sc.idx]) || 0;
      if (!targets[sc.appName]) targets[sc.appName] = {};
      targets[sc.appName][dateStr] = Math.round(val);
    });
    rowCount++;
  }

  if (!rowCount) { showUploadStatus('No valid date rows found — check the Date column format (YYYY-MM-DD)', 'error'); return; }

  parsedTargets = targets;
  const storeCount = Object.keys(targets).length;

  const expectedStores = Array.isArray(ALL_STORES)
    ? ALL_STORES.filter(s => s && s !== 'Retail Relief Managers')
    : [];
  const matchedSet = new Set(storeCols.map(sc => sc.appName));
  const missingStores = expectedStores.filter(s => !matchedSet.has(s));

  // Compute "Did you mean?" suggestions.
  const suggestions = findStoreColumnSuggestions(unmatchedHeaders, missingStores, dismissed);

  // Status banner
  const statusType = missingStores.length === 0 ? 'success' : 'warning';
  let statusMsg = `✅ Parsed ${rowCount} days × ${storeCount} stores — click Save & Apply below`;
  if (missingStores.length > 0 && expectedStores.length > 0) {
    statusMsg = `⚠️ Parsed ${rowCount} days × ${storeCount} stores. Found ${storeCols.length} of ${expectedStores.length} expected stores.${suggestions.length ? ' See suggestions below.' : ''}`;
  }
  showUploadStatus(statusMsg, statusType);

  // Show preview card with save button.
  const preview = document.getElementById('uploadPreviewCard');
  if (preview) preview.style.display = 'block';
  const previewEl = document.getElementById('uploadPreview');
  if (previewEl) {
    const datesShown = Object.keys(targets[Object.keys(targets)[0]] || {}).sort().slice(0,3);
    const storesShown = Object.keys(targets).slice(0, 5);
    let html = '<div style="font-size:12px;color:var(--sub);margin-bottom:8px">' + storeCount + ' stores × ' + rowCount + ' days parsed. Preview (first 5 stores × 3 dates):</div>';

    // Suggestion cards — namespaced for inline onclick resolution.
    html += renderStoreSuggestionCards(suggestions, 'Uploads.budget.acceptSuggestion', 'Uploads.budget.dismissSuggestion');

    // Stores still missing AND with no suggestion
    const suggestedStores = new Set(suggestions.map(s => s.store));
    const orphanMissing = missingStores.filter(s => !suggestedStores.has(s));
    if (orphanMissing.length > 0 && expectedStores.length > 0) {
      html += '<div style="background:var(--adm-warn-pale);border:1px solid var(--adm-warn-line);color:var(--warn-deep);padding:8px 10px;border-radius:6px;margin-bottom:8px;font-size:12px"><strong>⚠️ ' + orphanMissing.length + ' store' + (orphanMissing.length === 1 ? '' : 's') + ' missing — no close match in file:</strong><br>' + orphanMissing.map(s => '• ' + escapeHtml(s)).join('<br>') + '</div>';
    }

    html += '<table style="width:100%;font-size:12px;border-collapse:collapse"><thead><tr><th style="text-align:start;padding:4px 8px;border-bottom:1px solid var(--border)">Store</th>';
    datesShown.forEach(d => { html += '<th style="text-align:end;padding:4px 8px;border-bottom:1px solid var(--border)">' + d + '</th>'; });
    html += '</tr></thead><tbody>';
    storesShown.forEach(s => {
      html += '<tr><td style="padding:4px 8px">' + s + '</td>';
      datesShown.forEach(d => { html += '<td style="text-align:end;padding:4px 8px;font-variant-numeric:tabular-nums">' + (targets[s][d] || 0).toLocaleString() + '</td>'; });
      html += '</tr>';
    });
    html += '</tbody></table>';
    previewEl.innerHTML = html;
  }

  // Legacy button reference — only if it exists
  const btn = document.getElementById('applyTargetsBtn');
  if (btn) btn.style.display = 'inline-block';
}

export function acceptSuggestion(store, colIdx, headerLabel) {
  if (!budgetParseState) return;
  budgetParseState.accepted = budgetParseState.accepted.filter(e => e.appName !== store);
  budgetParseState.accepted.push({ idx: colIdx, appName: store });
  rebuildFromState();
  showToast('Mapped "' + headerLabel + '" → ' + store, 'success');
}

export function dismissSuggestion(store) {
  if (!budgetParseState) return;
  budgetParseState.dismissed.add(store);
  rebuildFromState();
}

// v65: Compute per-store totals in the upload vs the existing DB, return any
// stores whose total changes by more than CHANGE_THRESHOLD percent. Catches
// the misalignment-corruption case (April 28 incident) and similar accidents
// where someone uploads a wrong file or a file with shifted columns.
async function computeUploadDeltas(targets) {
  const CHANGE_THRESHOLD = 25; // percent
  let dbRows;
  try {
    dbRows = await DB.targets.list({});
  } catch(e) {
    console.warn('[Upload] Could not fetch existing targets for comparison:', e && e.message);
    return { skipped: 'fetch_failed', flagged: [] };
  }
  if (!Array.isArray(dbRows) || dbRows.length === 0) {
    return { skipped: 'no_existing_data', flagged: [] };
  }
  // Index DB by store→date
  const dbIndex = {};
  dbRows.forEach(r => {
    if (!dbIndex[r.store]) dbIndex[r.store] = {};
    dbIndex[r.store][r.date] = Number(r.value) || 0;
  });
  // For each parsed store, sum the dates present in the upload AND in the DB
  // (so we compare like-for-like — the upload's date range, not whole years).
  const flagged = [];
  Object.keys(targets).forEach(store => {
    const dates = Object.keys(targets[store]);
    let parsedSum = 0, dbSum = 0;
    dates.forEach(d => {
      parsedSum += (targets[store][d] || 0);
      dbSum += ((dbIndex[store] || {})[d] || 0);
    });
    // Skip stores with no DB data over this date range — first-time entries
    if (dbSum === 0) return;
    const pct = ((parsedSum - dbSum) / dbSum) * 100;
    if (Math.abs(pct) >= CHANGE_THRESHOLD) {
      flagged.push({ store, parsedSum, dbSum, pct });
    }
  });
  // Sort by absolute change magnitude, biggest first
  flagged.sort((a, b) => Math.abs(b.pct) - Math.abs(a.pct));
  return { skipped: null, flagged };
}

export async function saveTargets() {
  if (!parsedTargets) { showToast('No targets parsed yet', 'error'); return; }

  // v65: pre-flight sanity check. Compare upload to existing DB totals; if
  // any store's total moves by 25%+, ask the admin to confirm. Catches
  // misaligned spreadsheets, wrong-file uploads, and accidental zero-fills.
  showUploadStatus('🔍 Checking upload against existing targets...', 'info');
  const delta = await computeUploadDeltas(parsedTargets);
  if (delta.flagged && delta.flagged.length > 0) {
    const lines = delta.flagged.slice(0, 10).map(f => {
      const arrow = f.pct > 0 ? '↑' : '↓';
      const fmt = n => Math.round(n).toLocaleString();
      return `  ${arrow} ${f.store}: ${fmt(f.dbSum)} → ${fmt(f.parsedSum)} (${f.pct > 0 ? '+' : ''}${f.pct.toFixed(0)}%)`;
    }).join('\n');
    const more = delta.flagged.length > 10 ? `\n  ...and ${delta.flagged.length - 10} more stores with large changes` : '';
    const proceed = confirm(
      `⚠️ Large changes detected for ${delta.flagged.length} store${delta.flagged.length === 1 ? '' : 's'}.\n\n` +
      `Upload total vs existing total over the same dates:\n\n${lines}${more}\n\n` +
      `Continue with the upload? Click Cancel if these changes look wrong — the existing targets will be kept untouched.`
    );
    if (!proceed) {
      showUploadStatus('Upload cancelled. Existing targets unchanged.', 'info');
      return;
    }
  }

  try {
    // parsedTargets is {store: {date: value}}
    // index.html expects {date: {store: value}} — invert the shape before saving
    const inverted = {};
    Object.keys(parsedTargets).forEach(store => {
      Object.keys(parsedTargets[store]).forEach(date => {
        if (!inverted[date]) inverted[date] = {};
        inverted[date][store] = parsedTargets[store][date];
      });
    });
    localStorage.setItem('elcDailyTargets', JSON.stringify(inverted));
    localStorage.setItem('elcDailyTargetsDate', new Date().toISOString());
    // Flatten to array of {date, store, value} rows for DB bulk upload
    const rows = [];
    Object.keys(parsedTargets).forEach(store => {
      Object.keys(parsedTargets[store]).forEach(date => {
        rows.push({ date, store, value: parsedTargets[store][date] });
      });
    });
    if (rows.length) {
      showUploadStatus('⏳ Uploading ' + rows.length + ' targets to database...', 'info');
      DB.targets.upload(rows).then(result => {
        showToast('Targets saved — ' + (result.inserted || rows.length) + ' rows to DB', 'success');
        if (typeof renderTargetsStatus === 'function') renderTargetsStatus();
        showUploadStatus('✅ Saved to database and device storage. Rota app will use live targets.', 'success');
      }).catch(e => {
        console.warn('[DB] Targets upload failed:', e.message);
        showToast('Targets saved locally, DB upload failed — see console', 'error');
        showUploadStatus('⚠️ Saved to device only. DB upload failed: ' + e.message, 'error');
        if (typeof renderTargetsStatus === 'function') renderTargetsStatus();
      });
    } else {
      showToast('No target rows to save', 'error');
    }
  } catch(e) {
    showToast('Storage error: ' + e.message, 'error');
  }
}

export async function clearTargets() {
  if (!(await (window.uiConfirm || function (m) { return Promise.resolve(confirm(m)); })('Clear all saved targets? The rota will fall back to default estimates.', { danger: true, confirmLabel: 'Clear' }))) return;
  localStorage.removeItem('elcDailyTargets');
  localStorage.removeItem('elcDailyTargetsDate');
  parsedTargets = null;
  const card = document.getElementById('uploadPreviewCard');
  if (card) card.style.display = 'none';
  if (typeof renderTargetsStatus === 'function') renderTargetsStatus();
  showToast('Targets cleared', 'success');
}

function showUploadStatus(msg, type) {
  const el = document.getElementById('uploadStatus');
  const inner = document.getElementById('uploadStatusInner');
  if (!el || !inner) return;
  el.style.display = 'block';
  const colors = {
    success: {bg:'var(--adm-ok-pale)',color:'var(--ok-deep)',border:'var(--adm-ok-line)'},
    error:   {bg:'var(--adm-danger-pale)',color:'var(--danger-deep)',border:'var(--adm-danger-line)'},
    info:    {bg:'var(--info-bg)',color:'var(--adm-sky-text)',border:'var(--adm-info-line)'},
  };
  const c = colors[type] || colors.info;
  inner.style.cssText = `background:${c.bg};color:${c.color};border:1px solid ${c.border};padding:12px 14px;border-radius:10px;font-size:13px`;
  inner.textContent = msg;
}


// ════════════════════════════════════════════════════════════
// v102.0 additions — Budget UI helpers
// ════════════════════════════════════════════════════════════
// Two leftover budget-related render helpers folded into the existing
// budget module. rebuildBudgetFromState is the big one (138 lines) —
// recomputes the budget preview from current form state after any
// row edit. renderTargetsStatus draws the "Targets uploaded for year
// X" status line at the top of the targets management screen.

export function rebuildBudgetFromState() {
  if (!budgetParseState) return;
  const { rows, storeMapUpper, accepted, dismissed } = budgetParseState;

  // Find header row — contains 'Date'
  let headerRow = -1;
  for (let i = 0; i < Math.min(5, rows.length); i++) {
    const r = rows[i];
    if (r && r.some(c => String(c||'').toUpperCase().includes('DATE'))) {
      headerRow = i; break;
    }
  }
  if (headerRow === -1) { showUploadStatus('Could not find header row with Date column', 'error'); return; }

  const headers = rows[headerRow];

  let dateColIdx = -1;
  headers.forEach((h, i) => { if (String(h||'').trim().toUpperCase() === 'DATE') dateColIdx = i; });
  if (dateColIdx < 0) dateColIdx = 1; // fallback to col B

  // Match store columns from upload-name map; track unmatched for suggestions.
  const storeCols = [];
  const matchedIdx = new Set();
  headers.forEach((h, i) => {
    const key = String(h||'').trim().toUpperCase();
    const appName = storeMapUpper[key];
    if (appName) {
      storeCols.push({idx:i, xlsxName:String(h).trim(), appName});
      matchedIdx.add(i);
    }
  });

  // Apply accepted (user-picked "Did you mean?") extras.
  accepted.forEach(e => {
    if (!matchedIdx.has(e.idx)) {
      storeCols.push({idx:e.idx, xlsxName:String(headers[e.idx] || '').trim(), appName:e.appName});
      matchedIdx.add(e.idx);
    }
  });

  // Track unmatched headers for suggestions.
  const unmatchedHeaders = [];
  headers.forEach((h, i) => {
    if (i === dateColIdx || matchedIdx.has(i)) return;
    const label = String(h || '').trim();
    if (!label) return;
    const upper = label.toUpperCase();
    if (['DAY','MONTH','WEEK','UK MONTH','UK WEEK','DATE LY','DATE TY'].includes(upper)) return;
    unmatchedHeaders.push({ idx: i, header: label });
  });

  if (!storeCols.length) {
    showUploadStatus(`No recognised store columns found. Header row ${headerRow+1} has: ${headers.filter(Boolean).slice(0,5).join(', ')}... — check that store Upload Names in the Stores tab match the spreadsheet headers.`, 'error');
    return;
  }

  const targets = {};
  let rowCount = 0;
  for (let r = headerRow+1; r < rows.length; r++) {
    const row = rows[r];
    if (!row || !row[dateColIdx]) continue;
    let dateStr = String(row[dateColIdx]).trim();
    // Normalise date
    if (dateStr.includes('/')) {
      const parts = dateStr.split('/');
      if (parts.length === 3) {
        dateStr = parts[2]+'-'+parts[1].padStart(2,'0')+'-'+parts[0].padStart(2,'0');
      }
    } else if (dateStr.length > 10) {
      dateStr = dateStr.slice(0,10);
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) continue;

    storeCols.forEach(sc => {
      const val = parseFloat(row[sc.idx]) || 0;
      if (!targets[sc.appName]) targets[sc.appName] = {};
      targets[sc.appName][dateStr] = Math.round(val);
    });
    rowCount++;
  }

  if (!rowCount) { showUploadStatus('No valid date rows found — check the Date column format (YYYY-MM-DD)', 'error'); return; }

  parsedTargets = targets;
  const storeCount = Object.keys(targets).length;

  const expectedStores = (typeof ALL_STORES !== 'undefined' && Array.isArray(ALL_STORES))
    ? ALL_STORES.filter(s => s && s !== 'Retail Relief Managers')
    : [];
  const matchedSet = new Set(storeCols.map(sc => sc.appName));
  const missingStores = expectedStores.filter(s => !matchedSet.has(s));

  // Compute "Did you mean?" suggestions.
  const suggestions = Uploads.shared.findStoreColumnSuggestions(unmatchedHeaders, missingStores, dismissed);

  // Status banner
  const statusType = missingStores.length === 0 ? 'success' : 'warning';
  let statusMsg = `✅ Parsed ${rowCount} days × ${storeCount} stores — click Save & Apply below`;
  if (missingStores.length > 0 && expectedStores.length > 0) {
    statusMsg = `⚠️ Parsed ${rowCount} days × ${storeCount} stores. Found ${storeCols.length} of ${expectedStores.length} expected stores.${suggestions.length ? ' See suggestions below.' : ''}`;
  }
  showUploadStatus(statusMsg, statusType);

  // Show preview card with save button.
  const preview = document.getElementById('uploadPreviewCard');
  if (preview) preview.style.display = 'block';
  const previewEl = document.getElementById('uploadPreview');
  if (previewEl) {
    const datesShown = Object.keys(targets[Object.keys(targets)[0]] || {}).sort().slice(0,3);
    const storesShown = Object.keys(targets).slice(0, 5);
    let html = '<div style="font-size:12px;color:var(--sub);margin-bottom:8px">' + storeCount + ' stores × ' + rowCount + ' days parsed. Preview (first 5 stores × 3 dates):</div>';

    // Suggestion cards
    html += Uploads.shared.renderStoreSuggestionCards(suggestions, 'acceptBudgetSuggestion', 'dismissBudgetSuggestion');

    // Stores still missing AND with no suggestion
    const suggestedStores = new Set(suggestions.map(s => s.store));
    const orphanMissing = missingStores.filter(s => !suggestedStores.has(s));
    if (orphanMissing.length > 0 && expectedStores.length > 0) {
      html += '<div style="background:var(--adm-warn-pale);border:1px solid var(--adm-warn-line);color:var(--warn-deep);padding:8px 10px;border-radius:6px;margin-bottom:8px;font-size:12px"><strong>⚠️ ' + orphanMissing.length + ' store' + (orphanMissing.length === 1 ? '' : 's') + ' missing — no close match in file:</strong><br>' + orphanMissing.map(s => '• ' + escapeHtml(s)).join('<br>') + '</div>';
    }

    html += '<table style="width:100%;font-size:12px;border-collapse:collapse"><thead><tr><th style="text-align:start;padding:4px 8px;border-bottom:1px solid var(--border)">Store</th>';
    datesShown.forEach(d => { html += '<th style="text-align:end;padding:4px 8px;border-bottom:1px solid var(--border)">' + d + '</th>'; });
    html += '</tr></thead><tbody>';
    storesShown.forEach(s => {
      html += '<tr><td style="padding:4px 8px">' + s + '</td>';
      datesShown.forEach(d => { html += '<td style="text-align:end;padding:4px 8px;font-variant-numeric:tabular-nums">' + (targets[s][d] || 0).toLocaleString() + '</td>'; });
      html += '</tr>';
    });
    html += '</tbody></table>';
    previewEl.innerHTML = html;
  }

  // Legacy button reference — only if it exists
  const btn = document.getElementById('applyTargetsBtn');
  if (btn) btn.style.display = 'inline-block';
}

export function renderTargetsStatus() {
  const el = document.getElementById('targetsStatus');
  if (!el) return;   // v136.8: null-safe (the box may not be on screen)
  const saved = localStorage.getItem('elcDailyTargets');
  const savedDate = localStorage.getItem('elcDailyTargetsDate');
  if (!saved) {
    el.innerHTML = '<div style="color:var(--sub);font-size:13px">No targets uploaded yet. The rota app will use estimated weekly patterns.</div>';
    return;
  }
  try {
    const data = JSON.parse(saved);
    const dates = Object.keys(data).sort();
    const uploadedAt = savedDate ? new Date(savedDate).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit'}) : 'Unknown';
    el.innerHTML = `
      <div style="display:grid;grid-template-columns:repeat(3,1fr);gap:12px">
        <div style="background:var(--adm-ok-pale);border-radius:10px;padding:14px;text-align:center">
          <div style="font-size:22px;font-weight:800;color:var(--green)">${Object.keys(data[dates[0]]||{}).length}</div>
          <div style="font-size:11px;color:var(--sub);margin-top:2px">Stores</div>
        </div>
        <div style="background:var(--adm-ok-pale);border-radius:10px;padding:14px;text-align:center">
          <div style="font-size:22px;font-weight:800;color:var(--green)">${dates.length}</div>
          <div style="font-size:11px;color:var(--sub);margin-top:2px">Days</div>
        </div>
        <div style="background:var(--adm-ok-pale);border-radius:10px;padding:14px;text-align:center">
          <div style="font-size:14px;font-weight:700;color:var(--green)">${dates[0]}</div>
          <div style="font-size:11px;color:var(--sub);margin-top:2px">to ${dates[dates.length-1]}</div>
        </div>
      </div>
      <div style="margin-top:10px;font-size:12px;color:var(--sub)">Last uploaded: ${uploadedAt}</div>`;
  } catch(e) {
    el.innerHTML = '<div style="color:var(--red);font-size:13px">Saved data corrupted — please re-upload.</div>';
  }
}



// ════════════════════════════════════════════════════════════
// v104.0 additions — Budget (v104 extension)
// ════════════════════════════════════════════════════════════
// Two leftover budget admin tools: targets template export +
// annual top-up flow.

export function downloadTargetsTemplate() {
  // Build store list dynamically from live app data, using each store's upload name
  const storeSet = new Set(ALL_STORES);
  staffData.forEach(s => { if (s.store) storeSet.add(s.store); });
  const appStores = [...storeSet]
    .filter(s => s !== 'Retail Relief Managers' && getStoreType(s) !== 'virtual')
    .sort();
  // Column header = upload name for each store (editable in Stores tab)
  const storeXlsxNames = appStores.map(s => getStoreUploadName(s));
  const uaeStores = appStores.filter(s => !s.startsWith('Doha'));
  const qatarStores = appStores.filter(s => s.startsWith('Doha'));

  // v136.8: the spreadsheet library loads on first use; this runs again once it has (or says why it could not).
  if (typeof XLSX === 'undefined') { ensureXlsx().then(function () { downloadTargetsTemplate(); }, function (e) { showToast(e.message, 'error'); }); return; }

  const metaCols = ['Day', 'Date', 'Month', 'Week', 'UK Week'];
  const dayNames   = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'];
  const monthNames = ['January','February','March','April','May','June','July',
                      'August','September','October','November','December'];
  const year = new Date().getFullYear();

  // Row 0: totals row (col 4 = 'Total:', rest blank — matches real file structure)
  const totalsRow = Array(metaCols.length + storeXlsxNames.length + 2).fill('');
  totalsRow[4] = 'Total:';

  // Row 1: headers
  const headerRow = [...metaCols, ...storeXlsxNames, 'Total UAE', 'Total Qatar'];

  const rows = [totalsRow, headerRow];

  // Data rows: one per day for full year
  let d = new Date(year, 0, 1);
  const endD = new Date(year, 11, 31);
  while (d <= endD) {
    const iso = (function(dt) {
      const tmp = new Date(Date.UTC(dt.getFullYear(), dt.getMonth(), dt.getDate()));
      tmp.setUTCDate(tmp.getUTCDate() + 4 - (tmp.getUTCDay() || 7));
      const yearStart = new Date(Date.UTC(tmp.getUTCFullYear(), 0, 1));
      return Math.ceil((((tmp - yearStart) / 86400000) + 1) / 7);
    })(d);

    const dateStr = d.getFullYear() + '-' +
                    String(d.getMonth()+1).padStart(2,'0') + '-' +
                    String(d.getDate()).padStart(2,'0');
    const storeCells = storeXlsxNames.map(() => 0);
    const uaeTotal   = 0; // user fills; in real file these are formulas
    const qatarTotal = 0;
    rows.push([dayNames[d.getDay() === 0 ? 6 : d.getDay()-1],
               dateStr,
               monthNames[d.getMonth()],
               iso, iso,
               ...storeCells,
               uaeTotal, qatarTotal]);
    d.setDate(d.getDate() + 1);
  }

  const ws = XLSX.utils.aoa_to_sheet(rows);
  ws['!cols'] = [
    {wch:11},{wch:12},{wch:10},{wch:7},{wch:7},
    ...storeXlsxNames.map(() => ({wch:20})),
    {wch:12},{wch:12}
  ];
  // Freeze header rows (2) and meta cols (5)
  ws['!freeze'] = {xSplit:5, ySplit:2};

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Daily Target Upload');

  // Instructions sheet
  const instrRows = [
    ['GCC Daily Budget Template ' + year],
    [],
    ['HOW TO USE:'],
    ['• Cols A-E (Day/Date/Month/Week/UK Week) — do not edit'],
    ['• Cols F onwards: one column per store — enter daily target (currently 0)'],
    ['• Total UAE and Total Qatar columns are for reference (calculated in real file)'],
    [],
    ['TO UPLOAD: Admin → Targets & Budget → Upload Budget → drag and drop this file'],
    ['FORMAT: This matches the GCC_2026_Daily_Budgets file format exactly'],
  ];
  const ws2 = XLSX.utils.aoa_to_sheet(instrRows);
  ws2['!cols'] = [{wch:70}];
  XLSX.utils.book_append_sheet(wb, ws2, 'Instructions');

  XLSX.writeFile(wb, 'GCC_' + year + '_Daily_Budgets_Template.xlsx');
  showToast('Template downloaded — matches GCC Daily Budgets format exactly', 'success');
}

// v128.8 — the annual top-up is SERVER-SIDE now (docs/toil-carryover-design.md,
// Matt's answers 30 Jul). The old client version wrote the TOIL carry into
// balances.toil_balance — a column the compute-on-read model NEVER reads —
// so every hour of carried TOIL evaporated at midnight on 1 Jan; its
// localStorage balance writes were pre-database relics nothing read. The
// Worker now recomputes year-end finals itself, lands the carry in
// toil_adjust (negative TOIL carries as DEBT — decision #6), keeps a
// year-end snapshot on the audit row (decision #8), and guards re-runs
// behind an explicit confirm.
export async function applyAnnualTopUp(holDays, sickDays, toilCarry) {
  const status = document.getElementById('topUpStatus');
  const show = (bg, color, border, html) => {
    if (!status) return;
    status.style.display = 'block';
    status.style.background = bg; status.style.color = color; status.style.border = '1px solid ' + border;
    status.innerHTML = html;
  };
  const post = async (confirmRerun) => {
    const res = await fetch((DB.workerUrl ? DB.workerUrl() : '') + '/api/balances/annual-topup', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + (DB.getToken ? DB.getToken() : '') },
      cache: 'no-store',
      body: JSON.stringify({ holiday_days: holDays, sick_days: sickDays, toil_carry_cap: toilCarry, confirm: !!confirmRerun }),
    });
    return { ok: res.ok, data: await res.json().catch(() => null) };
  };
  try {
    show('var(--adm-warn-pale)', 'var(--warn-deep)', 'var(--adm-warn-line)', '⏳ Running the annual top-up on the server…');
    let { ok, data } = await post(false);
    if (ok && data && data.state === 'needs_confirm') {
      if (!confirm('⚠ ' + data.warning + '\n\nRe-run anyway?')) { show('var(--surface-3)', 'var(--text)', 'var(--border-strong)', 'Top-up not re-run.'); return; }
      ({ ok, data } = await post(true));
    }
    if (!ok || !data || data.state !== 'ok') {
      show('var(--adm-danger-pale)', 'var(--danger-deep)', 'var(--adm-danger-line)', '❌ Top-up failed: ' + ((data && data.error) || 'server error') + ' — nothing was changed.');
      showToast('Top-up failed — nothing changed', 'error');
      return;
    }
    show('var(--adm-ok-pale)', 'var(--ok-deep)', 'var(--adm-ok-line)',
      `✅ ${data.from_year} → ${data.to_year} top-up applied to <strong>${data.upserted} staff</strong>. ` +
      `TOIL carried for ${data.carried} (capped at ${toilCarry}h for ${data.capped}` +
      (data.debts ? `; ${data.debts} carry a negative balance as debt` : '') + '). ' +
      'Holiday and sick reset to entitlement (unused holiday does not carry — the year-end snapshot is on the audit log).' +
      (data.errors && data.errors.length ? '<br>⚠ ' + data.errors.length + ' staff failed — see the audit/error logs.' : ''));
    showToast(`Top-up applied to ${data.upserted} staff`, 'success');
    Uploads.balances.renderToilOverview();
  } catch (e) {
    show('var(--adm-danger-pale)', 'var(--danger-deep)', 'var(--adm-danger-line)', '❌ Top-up failed: ' + (e && e.message) + ' — nothing was changed.');
    showToast('Top-up failed — nothing changed', 'error');
  }
}

